import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PeptigenLogo } from "../components/Logo";
import usePageMeta from "../lib/usePageMeta";

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden py-12" style={{ backgroundColor: "#0B1220" }}>
      <div className="absolute inset-0 pg-grid-bg opacity-40" />
      <div className="absolute inset-0 pg-radial-cyan" />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8"><Link to="/"><PeptigenLogo size={42} /></Link></div>
        <div className="pg-card p-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} className="text-[#5EEAD4]" />
            <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]">Customer Account</span>
          </div>
          <h1 className="text-[#F7FAFC] text-2xl pg-heading mb-1" style={{ fontFamily: "'Rajdhani'" }}>{title}</h1>
          <p className="text-[#9CA8B8] text-sm mb-6">{sub}</p>
          {children}
        </div>
        <p className="mt-4 text-center text-xs text-[#9CA8B8]">
          Need help? <Link to="/contact" className="text-[#00C2D6] hover:underline">Contact our UK team</Link>
        </p>
      </div>
    </div>
  );
}

const detailText = (d, fallback) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback : fallback);

export function ForgotPassword() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  usePageMeta("Forgot Password", "Reset the password for your Peptigen Labs account.");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customers/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(detailText(err.response?.data?.detail, "Something went wrong — please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your inbox" sub="If that email is known to us, a reset link is on its way.">
        <div className="rounded-sm border border-[#5EEAD4]/30 bg-[#5EEAD4]/5 p-4 text-sm text-[#D5DCE8] leading-relaxed" data-testid="forgot-sent">
          <MailCheck size={18} className="text-[#5EEAD4] mb-2" />
          We&apos;ve sent a link to <strong className="text-[#F7FAFC]">{email}</strong>. It works once and expires in 60 minutes.
          Nothing there in a couple of minutes? Check junk, or <button type="button" onClick={() => setSent(false)} className="text-[#00C2D6] underline underline-offset-2">try again</button>.
        </div>
        <Link to="/account/login" className="block mt-5 text-center text-xs text-[#00C2D6] hover:underline" data-testid="forgot-back-login">Back to sign in</Link>
      </Shell>
    );
  }

  return (
    <Shell title="Forgot your password?" sub="Enter the email you order with and we'll send a link to choose a new one.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <div className="pg-micro mb-2">Email</div>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pg-input" placeholder="you@lab.ac.uk" data-testid="forgot-email" />
        </div>
        <button type="submit" disabled={busy} className="pg-btn-primary w-full" data-testid="forgot-submit">
          <KeyRound size={16} />{busy ? "Sending..." : "Send reset link"}
        </button>
      </form>
      <Link to="/account/login" className="block mt-5 text-center text-xs text-[#00C2D6] hover:underline" data-testid="forgot-back-login">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ checking: true, valid: false, email: "" });
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  usePageMeta("Choose a New Password", "Set a new password for your Peptigen Labs account.");

  useEffect(() => {
    if (!token) { setState({ checking: false, valid: false, email: "" }); return; }
    api.get(`/customers/reset-password/check?token=${encodeURIComponent(token)}`)
      .then((r) => setState({ checking: false, valid: !!r.data?.valid, email: r.data?.email || "" }))
      .catch(() => setState({ checking: false, valid: false, email: "" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.a.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw.a !== pw.b) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/customers/reset-password", { token, password: pw.a });
      localStorage.setItem("pg_customer_token", data.token);
      toast.success("Password updated — you're signed in");
      window.location.assign("/account");
    } catch (err) {
      setError(detailText(err.response?.data?.detail, "Couldn't reset the password. Please request a new link."));
    } finally {
      setBusy(false);
    }
  };

  if (state.checking) {
    return <Shell title="Checking your link…" sub="One moment."><div className="text-sm text-[#9CA8B8]" data-testid="reset-checking">Verifying reset link…</div></Shell>;
  }
  if (!state.valid) {
    return (
      <Shell title="This link isn't valid" sub="Reset links work once and expire after 60 minutes.">
        <div className="text-sm text-[#D5DCE8] mb-5" data-testid="reset-invalid">The link may have expired or already been used. Request a fresh one and we&apos;ll email it straight away.</div>
        <button type="button" onClick={() => navigate("/account/forgot")} className="pg-btn-primary w-full" data-testid="reset-request-new"><KeyRound size={16} />Request a new link</button>
      </Shell>
    );
  }
  return (
    <Shell title="Choose a new password" sub={`For ${state.email}. At least 8 characters.`}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <div className="pg-micro mb-2">New password</div>
          <input type="password" required minLength={8} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className="pg-input" autoComplete="new-password" data-testid="reset-password" />
        </div>
        <div>
          <div className="pg-micro mb-2">Repeat new password</div>
          <input type="password" required minLength={8} value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className="pg-input" autoComplete="new-password" data-testid="reset-password-confirm" />
        </div>
        {error && <div className="text-sm text-red-400 border border-red-400/40 bg-red-400/10 p-3 rounded-sm" data-testid="reset-error">{error}</div>}
        <button type="submit" disabled={busy} className="pg-btn-primary w-full" data-testid="reset-submit"><KeyRound size={16} />{busy ? "Saving..." : "Save password & sign in"}</button>
      </form>
    </Shell>
  );
}

export default ForgotPassword;
